/**
 * Google Analytics 4 configuration with first-party cookies
 * Compatible with Chrome's Privacy Sandbox
 */

declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
    dataLayer: any[];
  }
}

export interface GAConfig {
  measurementId: string;
  consentMode?: 'default' | 'granted' | 'denied';
  debugMode?: boolean;
  sendPageView?: boolean;
}

class GoogleAnalytics {
  private measurementId: string;
  private isInitialized = false;
  private consentMode: 'default' | 'granted' | 'denied';

  constructor(config: GAConfig) {
    this.measurementId = config.measurementId;
    this.consentMode = config.consentMode || 'default';
    
    if (typeof window !== 'undefined') {
      this.initialize(config);
    }
  }

  private initialize(config: GAConfig): void {
    if (this.isInitialized || !window.gtag) return;

    // Set up consent mode for Privacy Sandbox compatibility
    window.gtag('consent', this.consentMode, {
      'ad_storage': this.consentMode === 'granted' ? 'granted' : 'denied',
      'ad_user_data': this.consentMode === 'granted' ? 'granted' : 'denied',
      'ad_personalization': this.consentMode === 'granted' ? 'granted' : 'denied',
      'analytics_storage': this.consentMode === 'granted' ? 'granted' : 'denied',
      'wait_for_update': 500
    });

    // Configure GA4 with first-party cookie settings
    window.gtag('config', this.measurementId, {
      cookie_domain: window.location.hostname,
      cookie_flags: 'SameSite=Lax;Secure',
      cookie_prefix: '_ga',
      cookie_expires: 63072000, // 2 years in seconds
      cookie_update: true,
      send_page_view: config.sendPageView !== false,
      debug_mode: config.debugMode || false,
      // Privacy Sandbox compatibility
      first_party_collection: true,
      restrict_domain: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });

    this.isInitialized = true;
  }

  public trackPageView(path: string, title?: string): void {
    if (!this.isInitialized || !window.gtag) return;

    window.gtag('config', this.measurementId, {
      page_path: path,
      page_title: title || document.title
    });
  }

  public trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isInitialized || !window.gtag) return;

    window.gtag('event', eventName, {
      ...parameters,
      send_to: this.measurementId
    });
  }

  public consentUpdate(consent: 'granted' | 'denied'): void {
    if (!window.gtag) return;

    this.consentMode = consent;
    window.gtag('consent', 'update', {
      'ad_storage': consent === 'granted' ? 'granted' : 'denied',
      'ad_user_data': consent === 'granted' ? 'granted' : 'denied',
      'ad_personalization': consent === 'granted' ? 'granted' : 'denied',
      'analytics_storage': consent === 'granted' ? 'granted' : 'denied'
    });
  }

  public getMeasurementId(): string {
    return this.measurementId;
  }
}

// Singleton instance
let gaInstance: GoogleAnalytics | null = null;

export const initGoogleAnalytics = (config: GAConfig): GoogleAnalytics => {
  if (!gaInstance) {
    gaInstance = new GoogleAnalytics(config);
  }
  return gaInstance;
};

export const getGoogleAnalytics = (): GoogleAnalytics | null => {
  return gaInstance;
};

export default GoogleAnalytics;
export { GoogleAnalytics };
